import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { enviroment } from "../../../enviroment";
import {
  ClarityConsentState,
  readConsent,
  writeConsent,
} from "../../../utils/analytics/clarityConsent";
import {
  activateAnalytics,
  clarityConsent,
  resendIdentity,
  wasConsentWithdrawnThisPageView,
} from "../../../utils/analytics/clarity";
import { SecondaryButton } from "../Buttons/SecondaryButton";
import { CONSENT_BUTTON_CLASS } from "./ConsentBanner";

interface Props {
  open: boolean;
  onClose: () => void;
}

// "Cookie settings" (flag REACT_APP_CLARITY_CONSENT_ENABLED; only mounted
// by LoggedInMenu/MainFooter with the flag on): re-open the choice. Same
// texts and the same two equal buttons as the banner, plus the current
// choice and Close. The reload hint is shown when the choice is Accepted
// after a Decline - the dialog was opened with Declined, or Decline was
// pressed in this page view (also Accepted -> Decline -> Accept): a
// recorder that was stopped in this page view starts again only on the
// next page load (the bootstrap loads it at once then). Not rendered
// without the privacy policy URL (same rule as the banner).
export function ConsentDialog({ open, onClose }: Props): JSX.Element | null {
  const { t } = useTranslation();
  const privacyPolicyUrl = enviroment.privacyPolicyUrl;
  const [state, setState] = useState<ClarityConsentState>("unset");
  const [openedWith, setOpenedWith] = useState<ClarityConsentState>("unset");

  useEffect(() => {
    if (!open) return;
    const current = readConsent();
    setState(current);
    setOpenedWith(current);
  }, [open]);

  if (!privacyPolicyUrl) return null;

  function handleAccept() {
    writeConsent("granted");
    activateAnalytics();
    resendIdentity();
    setState("granted");
  }

  function handleDecline() {
    writeConsent("denied");
    clarityConsent(false);
    setState("denied");
  }

  const choiceLabel =
    state === "granted"
      ? t("consent.granted")
      : state === "denied"
        ? t("consent.denied")
        : "-";
  const showReloadHint =
    state === "granted" &&
    (openedWith === "denied" || wasConsentWithdrawnThisPageView());

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("consent.title")}</DialogTitle>
      <DialogContent>
        <p className="text-sm text-gray-800">{t("consent.body")}</p>
        <p className="text-sm mt-2">
          <a
            href={privacyPolicyUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="underline"
          >
            {t("consent.privacyLink")}
          </a>
        </p>
        <p className="text-sm font-semibold mt-4" data-testid="consent-current">
          {t("consent.current", { choice: choiceLabel })}
        </p>
        {showReloadHint && (
          <p className="text-xs text-gray-600 mt-1">{t("consent.reloadHint")}</p>
        )}
      </DialogContent>
      <DialogActions>
        <div className="flex flex-wrap gap-2 px-4 pb-2">
          <SecondaryButton
            type="button"
            width="sm"
            className={CONSENT_BUTTON_CLASS}
            onClick={handleDecline}
          >
            {t("consent.decline")}
          </SecondaryButton>
          <SecondaryButton
            type="button"
            width="sm"
            className={CONSENT_BUTTON_CLASS}
            onClick={handleAccept}
          >
            {t("consent.accept")}
          </SecondaryButton>
          <SecondaryButton type="button" width="sm" onClick={onClose}>
            {t("close")}
          </SecondaryButton>
        </div>
      </DialogActions>
    </Dialog>
  );
}
